// Flights widget — scheduled drone flights for the viewer (Ship 2, Aug 25 2026).
// Built for the dual-role board (Sam: 777 /my-work opens + 868 scheduled-tab
// views in 60d — this brings the same info to home without the hop).
// Soonest flight first with its date visible — that's the one thing the
// scheduled tab gave him; header shows the TRUE count.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import { logCardClick } from "../../../lib/uiEvents";

const SHOW = 5;

interface FlightRow {
  id: string;
  homeowner_name: string | null;
  property_street: string | null;
  property_city: string | null;
  drone_scheduled_date: string | null;
}

const fmtFlightDate = (iso: string | null) => {
  const day = String(iso ?? "").slice(0, 10);
  if (!day) return "";
  return new Date(`${day}T12:00:00Z`).toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
  });
};

export default function FlightsWidget({ memberId }: { memberId: string }) {
  const [rows, setRows] = useState<FlightRow[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("deals")
        .select("id,homeowner_name,property_street,property_city,drone_scheduled_date")
        .eq("assigned_drone_pilot_id", memberId)
        .eq("stage", "drone_scheduled")
        .order("drone_scheduled_date", { ascending: true, nullsFirst: false });
      if (cancelled) return;
      if (!error) setRows((data as FlightRow[]) ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [memberId]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 mb-1">
        <span>🚁</span>
        <span className="font-bold text-sm text-bardin-ink">My Flights</span>
        <span className="ml-auto text-xs font-bold text-slate-500">
          {rows ? `${rows.length} scheduled` : "…"}
        </span>
      </div>
      {!rows ? (
        <div className="space-y-2 mt-1">
          {[0, 1].map((i) => (
            <div key={i} className="h-9 rounded-lg bg-slate-100 animate-pulse" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-slate-400 py-2">No flights on the schedule — all caught up ✓</div>
      ) : (
        <>
          <div className="divide-y divide-slate-100">
            {rows.slice(0, SHOW).map((r) => (
              <Link
                key={r.id}
                to={`/deal/${r.id}`}
                onClick={() => logCardClick("widget_flights_row")}
                className="flex items-center gap-2 py-2"
              >
                <span className="text-[13px] font-bold text-bardin-ink truncate">
                  {r.homeowner_name ?? "—"}
                </span>
                <span className="text-[11px] text-slate-400 truncate flex-1">
                  {[r.property_street, r.property_city].filter(Boolean).join(", ")}
                </span>
                {r.drone_scheduled_date && (
                  <span className="px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 text-[10px] font-extrabold whitespace-nowrap">
                    {fmtFlightDate(r.drone_scheduled_date)}
                  </span>
                )}
              </Link>
            ))}
          </div>
          {rows.length > SHOW && (
            <div className="text-[10px] text-slate-400 font-semibold mt-1">+{rows.length - SHOW} more</div>
          )}
        </>
      )}
      <div className="flex mt-2 pt-2 border-t border-slate-100">
        <Link
          to="/drone"
          onClick={() => logCardClick("widget_flights_open")}
          className="ml-auto text-[11px] font-bold text-bardin-orange"
        >
          Drone board ›
        </Link>
      </div>
    </div>
  );
}
